import { useState, useEffect } from 'react';
import clsx from 'clsx';
import { Modal } from './Modal';
import { FormInput, FormTextarea, FormRow, FormField } from './FormFields';
import { LocationPicker } from './LocationPicker';
import { useUIStore } from '../store/uiStore';
import { useShallow } from 'zustand/react/shallow';
import { useTripStore } from '../store/tripStore';
import { useLibraryStore } from '../store/libraryStore';
import type { TransportMode, LocationRef } from '@poi-planner/shared';

const TRANSPORT_MODES: { mode: TransportMode; icon: string; label: string }[] = [
  { mode: 'flight', icon: 'flight', label: 'Flight' },
  { mode: 'train', icon: 'train', label: 'Train' },
  { mode: 'car', icon: 'directions_car', label: 'Car' },
  { mode: 'ferry', icon: 'directions_boat', label: 'Ferry' },
  { mode: 'bus', icon: 'directions_bus', label: 'Bus' },
];

export function TransportEditorModal() {
  const { activeEditor, closeTransportEditor, openPOIEditor } = useUIStore(
    useShallow((s) => ({
      activeEditor: s.activeEditor,
      closeTransportEditor: s.closeTransportEditor,
      openPOIEditor: s.openPOIEditor,
    }))
  );
  const transportEditorOpen = activeEditor?.type === 'transport';
  const transportEditorMode = transportEditorOpen ? activeEditor.mode : 'create';
  const transportEditorId = transportEditorOpen ? activeEditor.entityId : null;
  const transportEditorTargetDayId = transportEditorOpen ? activeEditor.context?.dayId ?? null : null;
  const transportEditorTargetOrder = transportEditorOpen ? activeEditor.context?.order ?? null : null;
  const { schedule, createTransportEvent, updateTransportEvent, deleteTransportEvent } = useTripStore();
  const pois = useLibraryStore((s) => s.pois);
  const accommodationConfigs = schedule.accommodationConfigs;

  const [mode, setMode] = useState<TransportMode>('flight');
  const [origin, setOrigin] = useState<LocationRef | null>(null);
  const [destination, setDestination] = useState<LocationRef | null>(null);
  const [departureTime, setDepartureTime] = useState('');
  const [duration, setDuration] = useState('60');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!transportEditorOpen) return;
    if (transportEditorMode === 'edit' && transportEditorId) {
      const event = schedule.transportEvents.find((te) => te.id === transportEditorId);
      if (event) {
        // Handle both new format (origin/destination) and legacy format (originPoiId/destinationPoiId)
        const legacyEvent = event as any;
        const originRef = event.origin ?? (legacyEvent.originPoiId ? { type: 'poi' as const, id: legacyEvent.originPoiId } : null);
        const destRef = event.destination ?? (legacyEvent.destinationPoiId ? { type: 'poi' as const, id: legacyEvent.destinationPoiId } : null);

        setMode(event.mode);
        setOrigin(originRef);
        setDestination(destRef);
        setDepartureTime(event.departureTime ?? '');
        setDuration(String(event.duration));
        setReference(event.reference ?? '');
        setNotes(event.notes ?? '');
        return;
      }
    }
    // Create mode defaults
    setMode('flight');
    setOrigin(null);
    setDestination(null);
    setDepartureTime('');
    setDuration('60');
    setReference('');
    setNotes('');
  }, [transportEditorOpen, transportEditorMode, transportEditorId, schedule.transportEvents]);

  const handleSave = () => {
    if (!origin || !destination) return;
    const data = {
      mode,
      origin,
      destination,
      departureTime: departureTime || '09:00',
      duration: parseInt(duration) || 60,
      reference: reference.trim() || null,
      notes: notes.trim() || null,
    };

    if (transportEditorMode === 'edit' && transportEditorId) {
      updateTransportEvent(transportEditorId, data);
    } else if (transportEditorTargetDayId) {
      createTransportEvent({
        ...data,
        dayId: transportEditorTargetDayId,
        order: transportEditorTargetOrder ?? 999,
      });
    }
    closeTransportEditor();
  };

  const handleDelete = () => {
    if (transportEditorId) {
      deleteTransportEvent(transportEditorId);
      closeTransportEditor();
    }
  };

  const isEdit = transportEditorMode === 'edit';
  const canSave = origin && destination && departureTime;

  return (
    <Modal
      open={transportEditorOpen}
      onClose={closeTransportEditor}
      title={isEdit ? 'Edit Transport' : 'Add Transport'}
      icon="route"
      width="480px"
      footer={
        <>
          {isEdit && (
            <button onClick={handleDelete} className="btn-danger mr-auto">
              Delete
            </button>
          )}
          <button onClick={closeTransportEditor} className="btn-secondary">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!canSave}
            className={clsx(
              'px-5 py-2 text-[13px] font-semibold font-sans rounded-lg border-none cursor-pointer transition-all',
              canSave
                ? 'bg-text-primary text-white'
                : 'bg-border text-text-muted cursor-default'
            )}
          >
            {isEdit ? 'Save' : 'Add'}
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {/* Mode selector */}
        <FormField label="Mode">
          <div className="flex gap-1.5">
            {TRANSPORT_MODES.map((tm) => {
              const active = mode === tm.mode;
              return (
                <button
                  key={tm.mode}
                  type="button"
                  onClick={() => setMode(tm.mode)}
                  className={clsx(
                    'flex-1 flex flex-col items-center gap-1 py-2.5 px-1 rounded-lg cursor-pointer transition-all font-sans',
                    active
                      ? 'border-2 border-text-primary bg-text-primary text-white'
                      : 'border border-border bg-white text-text-secondary'
                  )}
                >
                  <span className="material-symbols-outlined text-xl">{tm.icon}</span>
                  <span className="text-[10px] font-semibold">{tm.label}</span>
                </button>
              );
            })}
          </div>
        </FormField>

        <FormRow>
          <FormField label="Origin" flex={1}>
            <LocationPicker
              selected={origin}
              onSelect={setOrigin}
              onCreateNew={() => openPOIEditor('create')}
              pois={pois}
              accommodationConfigs={accommodationConfigs}
              placeholder="Select origin..."
            />
          </FormField>
          <FormField label="Destination" flex={1}>
            <LocationPicker
              selected={destination}
              onSelect={setDestination}
              onCreateNew={() => openPOIEditor('create')}
              pois={pois}
              accommodationConfigs={accommodationConfigs}
              placeholder="Select destination..."
            />
          </FormField>
        </FormRow>

        <FormRow>
          <FormField label="Departure Time *" flex={1}>
            <FormInput value={departureTime} onChange={setDepartureTime} type="time" />
          </FormField>
          <FormField label="Duration (minutes)" flex={1}>
            <FormInput value={duration} onChange={setDuration} type="number" min="0" placeholder="60" />
          </FormField>
        </FormRow>

        <FormField label="Reference">
          <FormInput value={reference} onChange={setReference} placeholder="e.g., BA178" />
        </FormField>

        <FormField label="Notes">
          <FormTextarea value={notes} onChange={setNotes} placeholder="Terminal, seat, etc." />
        </FormField>
      </div>
    </Modal>
  );
}
